import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import { TableSortArgs } from '../../components/TableV2';
import { PAGE_PROPS } from '../constants/common.constants';
import { SERVICES_INITIAL_STATE } from '../constants/servicesSlices.constants';

export const getAllServices = createAsyncThunk(
    'services/get',
    async (data: TableSortArgs) => {
        const response = await axios.post('/Services/Search', data);
        return response.data;
    },
);

export const getServicesList = createAsyncThunk('services/getAll', async () => {
    const response = await axios.get('/Services/GetAll');
    return response.data;
});

export const saveServiceMaster = createAsyncThunk(
    'services/save',
    async (
        data: {
            id?: number;
            serviceArea: string;
            serviceSubArea: string;
            createdBy?: string;
        },
        { rejectWithValue },
    ) => {
        try {
            // NOTE: App axios error interceptor returns the error object instead of
            // Promise.reject, so HTTP 4xx can resolve here as an AxiosError.
            const response: {
                data?: {
                    responseCode?: string;
                    responseMessage?: string;
                    result?: unknown;
                };
                response?: {
                    data?: {
                        responseCode?: string;
                        responseMessage?: string;
                        result?: unknown;
                    };
                };
            } = await axios.post('/Services/Save', {
                id: data.id ?? 0,
                serviceArea: data.serviceArea,
                serviceSubArea: data.serviceSubArea,
                createdBy: data.createdBy,
            });
            const body = response?.data ?? response?.response?.data;
            if (!body || String(body.responseCode) !== '200') {
                const msg = body?.responseMessage || '';
                return rejectWithValue(
                    body
                        ? {
                              ...body,
                              responseMessage: /already exists/i.test(
                                  String(msg),
                              )
                                  ? 'This service already exists in the global catalog. Please select it from the dropdown.'
                                  : msg || 'Failed to save service',
                          }
                        : {
                              responseCode: '400',
                              responseMessage: 'Failed to save service',
                          },
                );
            }
            return body;
        } catch (err: unknown) {
            const axiosErr = err as {
                response?: {
                    data?: { responseCode?: string; responseMessage?: string };
                };
            };
            return rejectWithValue(
                axiosErr?.response?.data || {
                    responseCode: '400',
                    responseMessage: 'Failed to save service',
                },
            );
        }
    },
);

export const deactivateServiceMaster = createAsyncThunk(
    'services/deactivate',
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await axios.post(`/Services/Deactivate/${id}`);
            return response.data;
        } catch (err: unknown) {
            const axiosErr = err as {
                response?: { data?: { responseCode?: string; responseMessage?: string } };
            };
            return rejectWithValue(
                axiosErr?.response?.data || {
                    responseCode: '400',
                    responseMessage: 'Failed to deactivate service',
                },
            );
        }
    },
);

export const reactivateServiceMaster = createAsyncThunk(
    'services/reactivate',
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await axios.post(`/Services/Reactivate/${id}`);
            return response.data;
        } catch (err: unknown) {
            const axiosErr = err as {
                response?: { data?: { responseCode?: string; responseMessage?: string } };
            };
            return rejectWithValue(
                axiosErr?.response?.data || {
                    responseCode: '400',
                    responseMessage: 'Failed to reactivate service',
                },
            );
        }
    },
);

export const serviceMasterSlice = createSlice({
    name: 'serviceMaster',
    initialState: SERVICES_INITIAL_STATE,
    reducers: {
        onServicePageChange: (state, action) => {
            state.pageProps = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getAllServices.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(getAllServices.fulfilled, (state, action) => {
                state.loading = 'succeeded';
                state.data = action.payload?.result || PAGE_PROPS;
                state.error = null;
            })
            .addCase(getAllServices.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
            })
            .addCase(getServicesList.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(getServicesList.fulfilled, (state, action) => {
                state.loading = 'succeeded';
                state.servicesList = action.payload?.result || [];
                state.error = null;
            })
            .addCase(getServicesList.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
            });
    },
});

export const { onServicePageChange } = serviceMasterSlice.actions;

export default serviceMasterSlice.reducer;
